import { useIntl } from '@capsicum/common';
import { ActivityIndicator } from '@capsicum/common-web';
import { type BidderRowProps, useBidHistory } from '@capsicum/features/fanshop/shared';
import React, { useEffect, useRef } from 'react';
import styled, { useTheme } from 'styled-components';

import { BidderRow } from '../../ui';

type BidHistoryProps = {
	auctionId: string;
};

export const BidHistory = ({ auctionId }: BidHistoryProps) => {
	const { $t } = useIntl();
	const { colors } = useTheme();

	const { bidListStructureData, chzShortCode, fetchNextPage, fiatShortCode, hasNextPage, isFetching, isLoading } =
		useBidHistory({ auctionId: auctionId! });

	const listEndRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const target = listEndRef.current;
		const distanceFromEnd = window.innerHeight * 0.5;

		const options = {
			rootMargin: `${distanceFromEnd}px`,
			threshold: 1
		};

		const handleIntersect: IntersectionObserverCallback = entries => {
			const [entry] = entries;

			if (entry?.target === target && entry?.isIntersecting) {
				if (hasNextPage && !isLoading) fetchNextPage();
			}
		};

		const observer = new IntersectionObserver(handleIntersect, options);

		target && observer.observe(target);

		return () => {
			target && observer.unobserve(target);
		};
	}, [fetchNextPage, hasNextPage, isLoading]);

	return (
		<S.PageWrapper>
			<S.Title>{$t('rewards.auctions.bidHistory')}</S.Title>

			{isLoading ? (
				<S.Loading>
					<ActivityIndicator color={colors.icons.main.enabled} />
				</S.Loading>
			) : (
				<S.RowsContainer>
					{bidListStructureData?.map(
						(
							bidUser: Omit<BidderRowProps, 'chzCurrencyShortCode' | 'chzRate' | 'fiatCurrencyShortCode'>,
							index: number
						) =>
							(bidUser.status === 'accepted' ||
								bidUser.status === 'outbidded' ||
								bidUser.status === 'validated') && (
								<S.StyledBidderRowWrapper key={`${bidUser?.amount}-${index}`}>
									<BidderRow
										amount={bidUser?.amount ? bidUser.amount : 0}
										amountChz={bidUser?.amountChz ? bidUser.amountChz : 0}
										chzCurrencyShortCode={chzShortCode}
										date={bidUser?.date}
										fiatCurrencyShortCode={fiatShortCode}
										iso2Country={bidUser?.iso2Country}
										isOwnBid={bidUser?.isOwnBid}
										mwsUserId={bidUser?.mwsUserId}
									/>
								</S.StyledBidderRowWrapper>
							)
					)}
				</S.RowsContainer>
			)}
			<div ref={listEndRef}></div>
			{isFetching && !isLoading && (
				<S.Loading>
					<ActivityIndicator color={colors.icons.main.enabled} />
				</S.Loading>
			)}
		</S.PageWrapper>
	);
};

namespace S {
	export const PageWrapper = styled.div`
		flex: 1;
		padding: ${({ theme }) => `${theme.spacings.xxlarge} 0`};

		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => `${theme.spacings.small}`};
		}
	`;

	export const Title = styled.div`
		${({ theme }) => theme.fonts.headings.title5};
		margin-bottom: ${({ theme }) => theme.spacings.large};
	`;

	export const RowsContainer = styled.div`
		flex: 1;
	`;

	export const StyledBidderRowWrapper = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.small};

		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
		}
	`;

	export const Loading = styled.div`
		display: flex;
		justify-content: center;
		padding: ${({ theme }) => `${theme.spacings.medium}`};
		margin: auto;
	`;
}
